import { BaseHit, Hit } from "instantsearch.js";
import get from "lodash/get";
import { useMemo } from "react";
import { AlgoliaTechBlog } from "@/lib/algolia";

interface Props<T extends Hit<BaseHit & AlgoliaTechBlog>> {
  attribute: keyof T;
  hit: T;
  keywords?: string[];
  className?: string;
  hiddenWhenNoMatch?: boolean;
}

export const BodyHighlight = <T extends Hit<BaseHit & AlgoliaTechBlog>>({
  attribute,
  hit,
  keywords = [],
  className,
  hiddenWhenNoMatch,
}: Props<T>) => {
  const result = get(hit._highlightResult, attribute as string);

  if (result == null || !("value" in result)) {
    return null;
  }

  const v = result.value;
  const words = result.matchedWords;

  if (Array.isArray(words)) {
    words.push(...(keywords as never[]));
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const value = useMemo(() => {
    const org = hiddenWhenNoMatch ? null : v;

    if (typeof v !== "string" || !Array.isArray(words) || words.length === 0) {
      return org;
    }

    // ts-morphなどを検索語句に入れると[ts, morph, ts-morph]がwordsに入ってくるため、語句が長い順に並び変えてmatchさせる
    const regex = new RegExp(
      `(${words.sort((a, b) => (typeof a == "string" && typeof b == "string" ? b.length - a.length : 0)).join("|")})`,
      "gi",
    );
    const match = regex.exec(v);

    if (match == null) {
      return org;
    }

    const start = match.index - 20 > 0 ? match.index - 20 : 0;
    const end = match.index + 200 < v.length ? match.index + 200 : v.length;

    return v.slice(start, end).replace(regex, `<span class="ais-Highlight-highlighted !text-default">$1</span>`);
  }, [v, words, hiddenWhenNoMatch]);

  return value == null ? <></> : <span className={className} dangerouslySetInnerHTML={{ __html: value }} />;
};
